import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyAccountData } from '../../hooks/useMyAccountData';
import { useUsageAlerts } from '../../hooks/useUsageWarnings';
import { Toast } from '../common/Toast';
import './MyAccount.css';
import { MyAccountUI } from './MyAccountUI';
import PortalHeader from './PortalHeader';

const MyAccount: React.FC = () => {
  const navigate = useNavigate();
  const {
    accountData,
    billingData,
    usageData,
    invoices,
    ledger,
    isLoading,
    toast,
    closeToast,
    showChangePlanModal,
    setShowChangePlanModal,
    showPaymentModal,
    setShowPaymentModal,
    showInvoicesModal,
    setShowInvoicesModal,
    openInvoicesModal,
    invoicesLoading,
    showEditAccountModal,
    setShowEditAccountModal,
    showEditAdminModal,
    setShowEditAdminModal,
    selectedPlan,
    setSelectedPlan,
    editAccountData,
    setEditAccountData,
    handleChangePlan,
    handlePaymentSuccess,
    handleUpdatePayment,
    handleEditAccount,
    handleEditAdmin,
  } = useMyAccountData();

  // Account usage is the authoritative source for low-credit warnings. The
  // warning hook derives a zero-safe percentage from used / lifetime purchased.
  const { warnings } = useUsageAlerts(
    usageData,
    accountData.accountId || 'default-subscription',
  );

  const handleLogout = () => {
    navigate('/login');
  };

  // Default fallbacks while data is loading
  const safeBillingData = billingData || {
    stripePlanType: '—',
    billingCycle: 'monthly' as const,
    paymentMethod: 'card' as const,
    stripeBillingEmail: '',
    startDate: '',
    annualAmount: 0,
    monthlyAmount: 0,
    paymentFrequency: 'monthly' as const,
  };

  const safeUsageData = usageData || {
    creditsUsed: 0,
    creditsTotal: 0,
    creditsRemaining: 0,
    cddsThisMonth: 0,
    cddsYearToDate: 0,
    completed: { count: 0, percentage: 0 },
    inProgress: { count: 0, percentage: 0 },
    failed: { count: 0, percentage: 0 },
  };

  if (isLoading) {
    return (
      <div className="portal-container my-account-container">
        <PortalHeader onLogout={handleLogout} />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <p style={{ color: '#888', fontSize: '1rem' }}>Loading account data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-container my-account-container">
      <PortalHeader onLogout={handleLogout} />
      <Toast toast={toast} onClose={closeToast} />
      <MyAccountUI
        accountData={accountData}
        billingData={safeBillingData}
        usageData={safeUsageData}
        invoices={invoices}
        ledger={ledger}
        warnings={warnings}
        showChangePlanModal={showChangePlanModal}
        setShowChangePlanModal={setShowChangePlanModal}
        showPaymentModal={showPaymentModal}
        setShowPaymentModal={setShowPaymentModal}
        showInvoicesModal={showInvoicesModal}
        setShowInvoicesModal={setShowInvoicesModal}
        openInvoicesModal={openInvoicesModal}
        invoicesLoading={invoicesLoading}
        showEditAccountModal={showEditAccountModal}
        setShowEditAccountModal={setShowEditAccountModal}
        showEditAdminModal={showEditAdminModal}
        setShowEditAdminModal={setShowEditAdminModal}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        editAccountData={editAccountData}
        setEditAccountData={setEditAccountData}
        handleChangePlan={handleChangePlan}
        handlePaymentSuccess={handlePaymentSuccess}
        handleUpdatePayment={handleUpdatePayment}
        handleEditAccount={handleEditAccount}
        handleEditAdmin={handleEditAdmin}
      />
    </div>
  );
};

export default MyAccount;
